"use client";

import type React from "react";

import { Box, Container, ListItem, Menu, Stack } from "@mui/material";
import "./NavBar.scss";

import logo from "assets/logo.svg";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Button from "components/atoms/Button";
import AuthService from "services/auth.service";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useTranslation } from "react-i18next";
import BasicMenu from "components/atoms/BasicMenu";
import MenuIcon from "@mui/icons-material/Menu";
import { useCallback, useEffect, useState } from "react";
import SideBar from "../SideBar/SideBar";
import { ChevronLeft } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { format } from "date-fns";
import { modalActions } from "redux/slices/modal";
import moment from "moment";
import edit from "assets/delivery/edit.svg";
import { formatDate } from "utils/formateDate.util";

interface FormValues {
  pickup?: {
    details?: { name: string };
    name?: string;
    value?: string;
  };
  destination?: {
    details?: { name: string };
    name?: string;
    value?: string;
  };
  date?: { date: string; error: boolean };
  endDate?: { date: string; error: boolean };
  [key: string]: any;
}

const NavBar = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation("translation");
  const user = useAppSelector((state) => state.auth.user);
  const location = useLocation();
  const isApp = location.search?.includes("platform=app");
  const { pathname } = location;
  const [state, setState] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [formValues, setFormValues] = useState<FormValues | undefined>();

  useEffect(() => {
    const saved = localStorage.getItem("DeliveryForm");
    if (saved) {
      setFormValues(JSON.parse(saved));
    }
  }, [localStorage]);

  const toggleDrawer = () => {
    setState(!state);
  };

  const arabicLang = useCallback(async () => {
    localStorage.setItem("lang", "ar");
    if (user) await AuthService.updateLanguage("ar");
    const newPathname = window.location.pathname.replace(/^\/(en|ar)/, `/ar`);
    window.location.replace(
      `${newPathname}${window.location.search}${window.location.hash}`
    );
  }, [user]);

  const englishLang = useCallback(async () => {
    localStorage.setItem("lang", "en");
    if (user) await AuthService.updateLanguage("en");
    const newPathname = window.location.pathname.replace(/^\/(en|ar)/, `/en`);
    window.location.replace(
      `${newPathname}${window.location.search}${window.location.hash}`
    );
  }, [user]);

  const links = [
    { path: `/business`, text: t("business") },
    {
      path:
        i18n.language === "en"
          ? `/global-reach`
          : "/انطلاقة-من-السعودية-نحو-آفاق-عالمية",
      text: t("GlobalReach"),
    },
    // {
    //   path: i18n.language === "en" ? `/coupon-promotion` : "/ترويج-القسيمة",
    //   text: t("Promotion"),
    // },
  ];

  const dropdownLinks = [
    {
      path:
        i18n.language === "en" ? `/airport-transfers` : "/النقل-من-وإلى-المطار",
      text: t("AirportTransfers"),
    },
    {
      path:
        i18n.language === "en" ? `/chauffeur-service` : "/خدمات-السائق-الخاص",
      text: t("ChauffeurService"),
    },
    {
      path:
        i18n.language === "en"
          ? `/limousine-service`
          : "/خدمة-الليموزين-الفاخرة",
      text: t("limousineService"),
    },
    {
      path:
        i18n.language === "en"
          ? `/city-to-city-transfer`
          : "/السفر-من-مدينة-الى-مدينة",
      text: t("CitytoCityTransfer"),
    },
    {
      path:
        i18n.language === "en" ? `/hire-by-the-hour` : "/خدمة-الحجز-بالساعة",
      text: t("HourlyBasis"),
    },
    {
      path:
        i18n.language === "en"
          ? `/event-transportation`
          : "/النقل-الفاخر-للفعاليات",
      text: t("eventBasis"),
    },
  ];

  const [isFixed, setIsFixed] = useState(false);
  const [opacity, setOpacity] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsFixed(false);
      if (
        pathname.includes("search-form") ||
        pathname.includes("search-delivery-form")
      )
        setIsFixed(true);
      else {
        setIsFixed(false);
        if (window.scrollY < 60) {
          setIsFixed(false);
          setOpacity(true);
        }
        if (window.scrollY > 60 && window.scrollY < 61) {
          setOpacity(false);
        }
        if (window.scrollY > 250) {
          setIsFixed(true);
          setOpacity(true);
        } else {
          setIsFixed(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [pathname]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isDropdownActive = dropdownLinks.some((link) => {
    try {
      const normalizedPathname = decodeURIComponent(pathname).toLowerCase();
      const normalizedLinkPath = decodeURIComponent(link.path).toLowerCase();
      return normalizedPathname.includes(normalizedLinkPath);
    } catch (e) {
      return pathname.includes(link.path);
    }
  });

  // Add this useEffect to listen to localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem("DeliveryForm");
      if (saved) {
        setFormValues(JSON.parse(saved));
      }
    };

    // Listen to storage events from other tabs/windows
    window.addEventListener("storage", handleStorageChange);

    // Listen to custom event for same-tab updates
    window.addEventListener("deliveryFormUpdated", handleStorageChange);

    // Initial load
    handleStorageChange();

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("deliveryFormUpdated", handleStorageChange);
    };
  }, []);

  // Check if we should show delivery info
  const showDeliveryInfo = pathname.includes("search-delivery-form");

  // Handle edit click
  const handleEditClick = () => {
    dispatch(
      modalActions.openModal({
        type: "EDIT_DELIVERY_ADDRESS",
        width: "700px",
        data: {},
      })
    );
  };

  return (
    <Box
      className="navbar__container"
      sx={{
        maxWidth: pathname === "/" ? "81rem" : "100%",
        position: isFixed ? "sticky" : "relative",
        opacity: opacity ? 1 : 0,
        transition: "all ease 0.3s",
        borderBottom:
          (pathname === "/" ||
            pathname.includes("airport-transfers") ||
            pathname.includes("limousine-service") ||
            pathname.includes("city-to-city-transfer") ||
            pathname.includes("hire-by-the-hour") ||
            pathname.includes("event-transportation") ||
            pathname.includes("chauffeur-service") ||
            pathname.includes("global-reach") ||
            pathname.includes("خدمات-السائق-الخاص") ||
            pathname.includes("خدمة-الليموزين-الفاخرة") ||
            pathname.includes("النقل-من-وإلى-المطار") ||
            pathname.includes("السفر-من-مدينة-الى-مدينة") ||
            pathname.includes("خدمة-الحجز-بالساعة") ||
            pathname.includes("النقل-الفاخر-للفعاليات") ||
            pathname.includes("انطلاقة-من-السعودية-نحو-آفاق-عالمية")) &&
          !isFixed
            ? { xs: "1px solid #DDDCE5", md: "none" }
            : "1px solid #DDDCE5 ",
        borderRadius: "22px",
        background: "#ffffff",
      }}
    >
      <Container maxWidth="lg">
        <div className="navbar">
          <Stack
            className="cursor"
            onClick={() => {
              if (!isApp) navigate("/");
            }}
            direction="row"
          >
            <img src={logo || "/placeholder.svg"} alt="logo" />
          </Stack>

          {/* Show delivery info or regular navbar links */}
          {showDeliveryInfo && formValues ? (
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 2,
                flex: { xs: 1, md: 1 },
                justifyContent: { xs: "center" },
                // bgcolor:{sm:"black"},
                ml: { xs: 0, md: 2 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, sm: 1.5 },
                  backgroundColor: "#F8F7FA",
                  borderRadius: "12px",
                  padding: { xs: "6px 8px", sm: "8px 12px" },
                  width: "fit-content",
                  maxWidth: { xs: "100%", sm: "500px", md: "fit-content" },
                  border: "1px solid #ECEBF2",
                  boxShadow: "0 2px 4px rgba(82, 10, 118, 0.04)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  {/* First line: Pickup and Destination */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 0.5, sm: 1 },
                      fontSize: { xs: "12px", sm: "13px", md: "14px" },
                      fontWeight: 500,
                      color: "#1A1A1A",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "150px",
                      }}
                    >
                      {formValues.pickup?.details?.name ||
                        formValues.pickup?.name ||
                        t("pickUp")}
                    </span>
                    {formValues.destination && (
                      <>
                        <span
                          style={{
                            color: "#520A76",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          →
                        </span>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "150px",
                          }}
                        >
                          {formValues.destination?.details?.name ||
                            formValues.destination?.name ||
                            t("destination")}
                        </span>
                      </>
                    )}
                  </Box>

                  {/* Second line: Dates */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 0.5, sm: 1 },
                      fontSize: { xs: "10px", sm: "11px", md: "12px" },
                      color: "#666",
                      fontWeight: 400,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span style={{ flexShrink: 0 }}>
                      {formatDate(formValues.pickupDateFormatted)}
                    </span>
                    {formValues.dropOffDateFormatted && (
                      <>
                        <span style={{ color: "#9E9E9E", flexShrink: 0 }}>
                          -
                        </span>
                        <span style={{ flexShrink: 0 }}>
                          {formatDate(formValues.dropOffDateFormatted) ||
                            t("endDate")}
                        </span>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Edit Icon */}
                <Box
                  onClick={handleEditClick}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: { xs: "32px", sm: "36px" },
                    height: { xs: "32px", sm: "36px" },
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #ECEBF2",
                    transition: "all 0.2s",
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: "#ECEBF2",
                      borderColor: "#520A76",
                      "& svg": {
                        color: "#FFFFFF",
                      },
                    },
                  }}
                >
                  <img src={edit} alt="" />
                </Box>
              </Box>
            </Box>
          ) : (
            <div className="routes">
              <span
                className={isDropdownActive ? "links active" : "links"}
                onClick={handleClick}
                style={{ display: "flex" }}
              >
                {t("services")}
                <ChevronLeft
                  sx={{
                    transition: "all 0.3s ease",
                    transform: Boolean(anchorEl)
                      ? "rotate(90deg)"
                      : "rotate(-90deg)",
                  }}
                />
              </span>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    filter: "none",
                    border: "1px solid #ECEBF2",
                    borderRadius: "16px",
                    mt: 1.5,
                  },
                }}
                transformOrigin={{
                  horizontal: i18n.language === "ar" ? "right" : "left",
                  vertical: "top",
                }}
                anchorOrigin={{
                  horizontal: i18n.language === "ar" ? "right" : "left",
                  vertical: "bottom",
                }}
              >
                {dropdownLinks.map(({ path, text }, i) => (
                  <ListItem sx={{ py: 0 }} key={i} onClick={handleClose}>
                    <NavLink
                      to={path}
                      className={({ isActive }) =>
                        isActive ? "links active" : "links"
                      }
                    >
                      {text}
                    </NavLink>
                  </ListItem>
                ))}
              </Menu>
              {links.map(({ path, text }, i) => (
                <NavLink key={i} to={path} className="links">
                  {text}
                </NavLink>
              ))}
            </div>
          )}

          <Stack
            direction={"row"}
            gap={2}
            alignItems={"center"}
            className="hide__login"
          >
            <BasicMenu
              list={[
                {
                  text: "EN",
                  onClick: englishLang,
                },
                {
                  text: "عربي",
                  onClick: arabicLang,
                },
              ]}
              avatar={false}
              color="#520a76"
            >
              <Stack
                direction="row"
                alignItems="center"
                height={"40px"}
                px={"8px"}
                borderRadius={"12px"}
                border={"1px solid #520a76"}
                columnGap={1}
              >
                <span>{i18n.language === "en" ? "EN" : "عربي"}</span>
              </Stack>
            </BasicMenu>
            <div className="hide__login">
              {user ? (
                <Stack direction="row" spacing={2}>
                  <BasicMenu
                    list={[
                      {
                        text: t("profile"),
                        onClick: () => navigate(`/profile`),
                      },
                      {
                        text: t("bookings"),
                        onClick: () => navigate(`/bookings`),
                      },
                      {
                        text: t("wallet"),
                        onClick: () => navigate(`/wallet`),
                      },
                      {
                        text: t("logout"),
                        onClick: () => AuthService.logout(),
                      },
                    ]}
                    avatar={true}
                  >
                    {user.first_name.charAt() + user.last_name.charAt()}
                  </BasicMenu>
                </Stack>
              ) : (
                <Button
                  onClick={() => navigate(`/login`)}
                  variant="contained"
                  color="secondary"
                  sx={{
                    height: "42px",
                    borderRadius: "12px",
                    textTransform: "capitalize",
                  }}
                >
                  {t("Get Started")}
                </Button>
              )}
            </div>
          </Stack>
        </div>
      </Container>

      {!isApp && (
        <>
          <MenuIcon
            onClick={toggleDrawer}
            className={
              i18n.language === "ar"
                ? "burger__icons ar_burger"
                : "burger__icons"
            }
          />
          <SideBar
            state={state}
            toggleDrawer={toggleDrawer}
            englishLang={englishLang}
            arabicLang={arabicLang}
          />
        </>
      )}
    </Box>
  );
};

export default NavBar;
